import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleMap, Polyline, useLoadScript } from '@react-google-maps/api';
import { Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert } from '@mui/material';
import dayjs from 'dayjs';
import supabase from '../supabaseClient';

const CAR_COLORS = [
  { value: '#FF0000', label: 'Red' },
  { value: '#800080', label: 'Purple' },
  { value: '#0000FF', label: 'Blue' },
  { value: '#FFA500', label: 'Orange' },
  { value: '#008080', label: 'Teal' },
  { value: '#FFD700', label: 'Gold' },
  { value: '#FF69B4', label: 'Pink' },
  { value: '#00FF00', label: 'Green' },
];

const GOOGLE_MAPS_LIBRARIES = ['marker'];

const Map = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [selectedCarPositions, setSelectedCarPositions] = useState([]);
  const [carColors, setCarColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const polylineRef = useRef(null);
  const mapRef = useRef(null);
  const advancedMarkerRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const startMarkerRef = useRef(null);

  const selectedCar = cars.find(c => c.id === selectedCarId);

  const center = { lat: 0, lng: 0 };
  const mapContainerStyle = { width: '100vw', height: '100vh' };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCars = useCallback(async () => {
    try {
      setLoading(true);
      const { data: carsData, error } = await supabase
        .from('cars')
        .select('*');
      if (error) throw error;
      const colorMap = {};
      carsData.forEach((car, index) => {
        colorMap[car.id] = CAR_COLORS[index % CAR_COLORS.length];
      });
      setCarColors(colorMap);
      setCars(carsData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch car data');
      console.error('Error fetching cars:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCarPositions = useCallback(async (carId) => {
    if (!carId) return;
    try {
      setLoading(true);
      // Date filtering logic (last 30 days)
      const now = new Date();
      const startOfRange = new Date(now);
      startOfRange.setUTCDate(now.getUTCDate() - 29); // 30 days including today
      startOfRange.setUTCHours(0, 0, 0, 0);
      const endOfRange = new Date(now);
      endOfRange.setUTCHours(23, 59, 59, 999);
      let query = supabase
        .from('car_positions')
        .select('*')
        .eq('car_id', carId)
        .gte('timestamp', startOfRange.toISOString())
        .lte('timestamp', endOfRange.toISOString())
        .order('timestamp', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      setSelectedCarPositions(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch car positions');
      console.error('Error fetching car positions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  useEffect(() => {
    if (cars.length > 0 && !selectedCarId) {
      setSelectedCarId(cars[0].id);
    }
  }, [cars, selectedCarId]);

  useEffect(() => {
    if (selectedCarId) {
      fetchCarPositions(selectedCarId);
    }
  }, [selectedCarId, fetchCarPositions]);

  const handleCarChange = (event) => {
    const carId = event.target.value;
    clearMap();
    setSelectedCarId(carId);
    setSelectedCarPositions([]);
  };

  const clearMap = () => {
    setShowMap(false);
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (advancedMarkerRef.current) {
      advancedMarkerRef.current.map = null;
      advancedMarkerRef.current = null;
    }
    setTimeout(() => setShowMap(true), 100);
  };

  useEffect(() => {
    if (selectedCarId) {
      setShowOverlay(true);
    }
  }, [selectedCarId]);

  const currentMonthPositions = useMemo(() => {
    const now = dayjs();
    return selectedCarPositions.filter(pos => {
      const ts = dayjs(pos.timestamp);
      return ts.year() === now.year() && ts.month() === now.month();
    });
  }, [selectedCarPositions]);

  const lastPosition = currentMonthPositions.length > 0 ? currentMonthPositions[0] : null;
  const firstPosition = currentMonthPositions.length > 0 ? currentMonthPositions[currentMonthPositions.length - 1] : null;

  useEffect(() => {
    let markerInstance = null;
    async function createMarker() {
      if (
        mapRef.current &&
        mapReady &&
        showOverlay &&
        selectedCar &&
        lastPosition &&
        window.google &&
        window.google.maps &&
        window.google.maps.importLibrary
      ) {
        if (advancedMarkerRef.current) {
          advancedMarkerRef.current.map = null;
          advancedMarkerRef.current = null;
        }

        const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary("marker");
        const pin = new PinElement({
          background: carColors[selectedCar.id]?.value || '#000',
        });
        markerInstance = new AdvancedMarkerElement({
          map: mapRef.current,
          position: { lat: lastPosition.latitude, lng: lastPosition.longitude },
          title: selectedCar.name,
          zIndex: 9999,
          content: pin.element,
        });
        advancedMarkerRef.current = markerInstance;
      }
    }

    createMarker();

    return () => {
      if (markerInstance) {
        markerInstance.map = null;
        markerInstance = null;
      }
    };
  }, [showOverlay, selectedCar, lastPosition, mapReady, carColors]);

  useEffect(() => {
    let markerInstance = null;
    async function createStartMarker() {
      if (
        mapRef.current &&
        mapReady &&
        showOverlay &&
        selectedCar &&
        firstPosition &&
        window.google &&
        window.google.maps &&
        window.google.maps.importLibrary
      ) {
        if (startMarkerRef.current) {
          startMarkerRef.current.map = null;
          startMarkerRef.current = null;
        }
        const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary("marker");
        const pin = new PinElement({
          background: '#fff',
          borderColor: carColors[selectedCar.id]?.value || '#000',
          glyph: 'S',
          glyphColor: carColors[selectedCar.id]?.value || '#000',
        });
        markerInstance = new AdvancedMarkerElement({
          map: mapRef.current,
          position: { lat: firstPosition.latitude, lng: firstPosition.longitude },
          title: `${selectedCar.name} Start`,
          zIndex: 9998,
          content: pin.element,
        });
        startMarkerRef.current = markerInstance;
      }
    }
    createStartMarker();
    return () => {
      if (markerInstance) {
        markerInstance.map = null;
        markerInstance = null;
      }
      if (startMarkerRef.current) {
        startMarkerRef.current.map = null;
        startMarkerRef.current = null;
      }
    };
  }, [showOverlay, selectedCar, firstPosition, mapReady, carColors]);

  if (loadError) return <div className="error">Error loading maps</div>;
  if (!isLoaded) return <div className="loading">Loading maps...</div>;
  
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', bottom: 20, left: 20, zIndex: 1, backgroundColor: 'white', padding: 2, borderRadius: 1, boxShadow: 3, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 0 }}>
        <FormControl fullWidth>
          <InputLabel id="car-select-label" sx={{ color: 'black' }}>Select Car</InputLabel>
          <Select
            labelId="car-select-label"
            value={selectedCarId || ''}
            label="Select Car"
            onChange={handleCarChange}
            displayEmpty
            sx={{
              '& .MuiSelect-select': { color: 'black' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
              backgroundColor: 'white',
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'white',
                  '& .MuiMenuItem-root': {
                    color: 'black',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  },
                },
              },
            }}
          >
            {cars.map((car) => (
              <MenuItem key={car.id} value={car.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: carColors[car.id]?.value || '#000',
                  }} />
                  <span style={{ color: '#000' }}>{car.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Last updated time below dropdown */}
        {lastPosition && (
          <Box>
            <span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
              Last updated: {dayjs(lastPosition.timestamp).format('MMM D, YYYY h:mm:ss A')}
            </span>
          </Box>
        )}

        {(loading || error) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            {loading && <CircularProgress size={24} sx={{ mr: 2 }} />}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </Box>

      {showMap && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={lastPosition ? { lat: lastPosition.latitude, lng: lastPosition.longitude } : center}
          zoom={lastPosition ? 16 : 2}
          onLoad={map => { 
            mapRef.current = map;
            setMapReady(true);
          }}
          options={{
            mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
            disableDefaultUI: false
          }}
        >
          {showOverlay && selectedCar && currentMonthPositions.length > 0 && (
            <Polyline
              path={currentMonthPositions.map(pos => ({ lat: pos.latitude, lng: pos.longitude }))}
              options={{
                strokeColor: carColors[selectedCar.id]?.value || '#000',
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
              onLoad={polyline => polylineRef.current = polyline}
              onUnmount={() => polylineRef.current = null}
            />
          )}
          {showOverlay && selectedCar && firstPosition && null}
        </GoogleMap>
      )}
    </div>
  );
};

export default Map;